import type { MovementSummaryRow, RecoveryCreditRow, StockValuationRow } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { Segmented } from '../components/Segmented';
import { ApiError, api, reportCsvUrl, type ReportParams } from '../lib/api';
import { formatQuantity, humanise } from '../lib/format';

type Report = 'stock-valuation' | 'movements' | 'recovery-credit';

const REPORT_LABELS: Record<Report, string> = {
  'stock-valuation': 'Stock valuation',
  movements: 'Movements',
  'recovery-credit': 'Recovery credit',
};

const VALUATION_COLUMNS: DataColumn<StockValuationRow>[] = [
  {
    key: 'item',
    header: 'Item',
    primary: true,
    cell: (row) => (
      <>
        <span className="font-mono text-xs">{row.itemCode}</span>
        <br />
        <span className="text-ink-body">{row.itemName}</span>
      </>
    ),
  },
  { key: 'kind', header: 'Kind', cell: (row) => <span className="text-xs">{humanise(row.kind)}</span> },
  { key: 'location', header: 'Location', cell: (row) => <span className="text-xs">{row.locationCode}</span> },
  { key: 'lots', header: 'Lots', cell: (row) => row.lots },
  {
    key: 'quantity',
    header: 'Quantity',
    cell: (row) => (
      <span className="whitespace-nowrap font-semibold text-ink">
        {formatQuantity(row.quantity, row.baseUom)}
      </span>
    ),
  },
  {
    key: 'value',
    header: 'Value',
    align: 'right',
    cell: (row) => <span className="whitespace-nowrap">{formatQuantity(row.value)}</span>,
  },
  {
    key: 'held',
    header: 'Held',
    cell: (row) => (
      <span className="whitespace-nowrap text-xs">
        {row.heldQuantity === '0' ? '—' : formatQuantity(row.heldQuantity)}
      </span>
    ),
  },
];

const MOVEMENT_COLUMNS: DataColumn<MovementSummaryRow>[] = [
  { key: 'type', header: 'Movement type', primary: true, cell: (row) => <span className="font-medium text-ink">{humanise(row.type)}</span> },
  {
    key: 'direction',
    header: 'Direction',
    cell: (row) => <Badge tone={row.direction === 'in' ? 'green' : 'hazard'}>{row.direction}</Badge>,
  },
  { key: 'movements', header: 'Movements', cell: (row) => row.movements },
  {
    key: 'quantity',
    header: 'Net quantity',
    align: 'right',
    cell: (row) => (
      <span
        className={`whitespace-nowrap font-semibold ${
          row.direction === 'in' ? 'text-brand-deep' : 'text-red-700'
        }`}
      >
        {formatQuantity(row.quantity)}
      </span>
    ),
  },
  {
    key: 'value',
    header: 'Net value',
    cell: (row) => <span className="whitespace-nowrap">{formatQuantity(row.value)}</span>,
  },
];

const RECOVERY_COLUMNS: DataColumn<RecoveryCreditRow>[] = [
  {
    key: 'item',
    header: 'Item',
    primary: true,
    cell: (row) => (
      <>
        <span className="font-mono text-xs">{row.itemCode}</span>
        <br />
        <span className="text-ink-body">{row.itemName}</span>
      </>
    ),
  },
  { key: 'recoveries', header: 'Recoveries', cell: (row) => row.recoveries },
  {
    key: 'quantity',
    header: 'Recovered',
    cell: (row) => (
      <span className="whitespace-nowrap font-semibold text-ink">
        {formatQuantity(row.quantity, row.uom)}
      </span>
    ),
  },
  {
    key: 'credited',
    header: 'Credited value',
    align: 'right',
    cell: (row) => <span className="whitespace-nowrap">{formatQuantity(row.creditedValue)}</span>,
  },
  {
    key: 'released',
    header: 'Released',
    cell: (row) => (
      <span className="whitespace-nowrap">{formatQuantity(row.releasedQuantity, row.uom)}</span>
    ),
  },
  {
    key: 'hold',
    header: 'Still on hold',
    cell: (row) =>
      row.quarantinedQuantity === '0' ? (
        <span className="whitespace-nowrap">—</span>
      ) : (
        <Badge tone="hazard">{formatQuantity(row.quarantinedQuantity, row.uom)}</Badge>
      ),
  },
];

/**
 * Reports.
 *
 * Every figure is computed from the ledger when the page loads, so a report can
 * never be a stale total. The CSV download runs the same query as the table.
 */
export function ReportsPage() {
  const [report, setReport] = useState<Report>('stock-valuation');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params: ReportParams = {
    ...(from ? { from: new Date(`${from}T00:00:00Z`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59Z`).toISOString() } : {}),
  };

  const valuation = useQuery({
    queryKey: ['report', 'stock-valuation'],
    queryFn: () => api.reports.stockValuation(),
    enabled: report === 'stock-valuation',
  });
  const movements = useQuery({
    queryKey: ['report', 'movements', params],
    queryFn: () => api.reports.movements(params),
    enabled: report === 'movements',
  });
  const credit = useQuery({
    queryKey: ['report', 'recovery-credit', params],
    queryFn: () => api.reports.recoveryCredit(params),
    enabled: report === 'recovery-credit',
  });

  const active = report === 'stock-valuation' ? valuation : report === 'movements' ? movements : credit;
  const error = active.error;

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Reports"
        description="Computed from the ledger on demand rather than from stored totals, so a report cannot disagree with the movements behind it."
        actions={
          <a
            className="inline-flex h-9 items-center rounded-full border border-brand-deep bg-white px-4 text-xs font-bold uppercase tracking-wide text-brand-deep transition hover:bg-brand-deep hover:text-white"
            href={reportCsvUrl(report, report === 'stock-valuation' ? {} : params)}
          >
            Download CSV
          </a>
        }
      />

      <Segmented
        label="Report"
        value={report}
        onChange={setReport}
        options={(Object.keys(REPORT_LABELS) as Report[]).map((candidate) => ({
          value: candidate,
          label: REPORT_LABELS[candidate],
        }))}
      />

      {report !== 'stock-valuation' ? (
        <Card className="mb-4">
          <CardBody>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="From" hint="Blank means the beginning of time">
                <input
                  name="reportFrom"
                  className={inputClass}
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </Field>
              <Field label="To" hint="Blank means now">
                <input
                  name="reportTo"
                  className={inputClass}
                  type="date"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              </Field>
              <div className="flex items-end">
                <Button
                  tone="ghost"
                  onClick={() => {
                    setFrom('');
                    setTo('');
                  }}
                >
                  Reset dates
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {error ? (
        <div className="mb-4">
          <ErrorNote>
            {error instanceof ApiError ? error.message : 'Could not build that report.'}
          </ErrorNote>
        </div>
      ) : null}

      <Card>
        <CardBody>
          {active.isPending ? <Spinner label="Building the report…" /> : null}

          {report === 'stock-valuation' && valuation.isSuccess ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink-body">
                  Total value on hand:{' '}
                  <strong>{formatQuantity(valuation.data.totals.quantityValue)}</strong>
                </span>
                {valuation.data.totals.heldQuantity !== '0' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Badge tone="hazard">
                      {formatQuantity(valuation.data.totals.heldQuantity)} counted but not usable
                    </Badge>
                    <Tooltip
                      subject="Counted but not usable"
                      text="Stock that is on quality hold or rejected. It is real, and it is included in the value above because it still belongs to the business — but production cannot draw on it until it is released."
                    />
                  </span>
                ) : null}
              </div>
              <DataTable
                rows={valuation.data.rows}
                columns={VALUATION_COLUMNS}
                rowKey={(row) => `${row.itemId}:${row.locationId}`}
                empty="Nothing in stock."
              />
            </>
          ) : null}

          {report === 'movements' && movements.isSuccess ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink-body">
                  {movements.data.totals.movements} movements · net value{' '}
                  <strong>{formatQuantity(movements.data.totals.value)}</strong>
                </span>
              </div>
              <DataTable
                rows={movements.data.rows}
                columns={MOVEMENT_COLUMNS}
                rowKey={(row) => row.type}
                empty="Nothing moved in that period."
              />
            </>
          ) : null}

          {report === 'recovery-credit' && credit.isSuccess ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink-body">
                  {credit.data.totals.recoveries} recover
                  {credit.data.totals.recoveries === 1 ? 'y' : 'ies'} · credited{' '}
                  <strong>{formatQuantity(credit.data.totals.creditedValue)}</strong> back to
                  inventory
                </span>
              </div>
              <DataTable
                rows={credit.data.rows}
                columns={RECOVERY_COLUMNS}
                rowKey={(row) => `${row.itemId}:${row.uom}`}
                empty="Nothing recovered in that period."
              />
            </>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
